import { useRef } from 'react';
import { motion, useInView } from 'motion/react';

const experience = [
  {
    years: '2023 – Present',
    role: 'Senior Account Executive',
    company: 'Cloudflare',
    color: '#F6821F',
    bullets: [
      '100%+ quota attainment consistently',
      '4× Top Performer',
      'Top regional performer in enterprise segment',
    ],
  },
  {
    years: '2022 – 2023',
    role: 'Account Executive',
    company: 'Cloudflare',
    color: '#F6821F',
    bullets: [
      'Closed complex enterprise deals across networking & security',
      'Built and expanded strategic accounts',
    ],
  },
  {
    years: '2021 – 2022',
    role: 'Channel Account Executive / BDR',
    company: 'Cloudflare',
    color: '#F6821F',
    bullets: [
      'Launched partner channel relationships',
      'Consistently exceeded pipeline targets',
    ],
  },
  {
    years: '2020 – 2021',
    role: 'Account Executive',
    company: 'Paycor',
    color: '#4a7c59',
    bullets: [
      'Sold HCM solutions to mid-market companies',
      'Top new business performer in territory',
    ],
  },
  {
    years: '2019 – 2020',
    role: 'Account Executive',
    company: 'Vercara',
    color: '#4a7c59',
    bullets: [
      'Enterprise DNS & cybersecurity solutions',
      'Managed complex multi-stakeholder sales cycles',
    ],
  },
  {
    years: '2016 – 2019',
    role: 'Director of Procurement',
    company: 'Lighthouse Solar',
    color: '#e8c84a',
    bullets: [
      'Managed $50M+ in annual materials procurement',
      'Built supplier network from the ground up',
      'Scaled operations through rapid company growth',
    ],
  },
  {
    years: '2004 – 2016',
    role: 'Pre-loader → Operations Supervisor',
    company: 'UPS',
    color: '#8B6914',
    bullets: [
      'Started loading package cars at 3am',
      'Progressed through every operational role in the building',
      'Managed teams, logistics, and time-critical operations',
    ],
  },
];

function ExpCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      style={{
        borderLeft: `2px solid ${item.color}`,
        paddingLeft: '1.5rem',
        paddingBottom: '2.5rem',
      }}
    >
      <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
        {item.years}
      </div>
      <div style={{ fontFamily: 'var(--serif)', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.2rem' }}>
        {item.role}
      </div>
      <div style={{ fontSize: '0.78rem', letterSpacing: '0.06em', color: item.color, marginBottom: '0.75rem', textTransform: 'uppercase', fontWeight: 500 }}>
        {item.company}
      </div>
      <ul style={{ paddingLeft: '1rem' }}>
        {item.bullets.map((b, i) => (
          <li key={i} style={{ fontSize: '0.88rem', lineHeight: 1.75, color: 'var(--text-muted)', fontWeight: 300, marginBottom: '0.15rem' }}>
            {b}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function About() {
  return (
    <div style={{ background: 'var(--bg)', paddingTop: 64, minHeight: '100vh' }}>

      {/* Split Panel Hero */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '100vh' }}>

        {/* Left — photo */}
        <motion.div
          style={{ position: 'relative', overflow: 'hidden', background: '#0a0d0b' }}
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/photos/photo4.jpg`}
            alt="Burke Ruder"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center top',
              display: 'block',
            }}
          />
          {/* Gradient fade into dark on the right edge */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to right, transparent 60%, var(--bg) 100%)',
          }} />
          {/* Bottom fade */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, transparent 70%, var(--bg) 100%)',
          }} />
        </motion.div>

        {/* Right — name + title */}
        <motion.div
          style={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            padding: '4rem 5vw 4rem 3rem',
            background: 'var(--bg)',
          }}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.p
            style={{ fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.6 }}
          >
            <span style={{ display: 'block', width: 28, height: 1, background: 'var(--accent)' }} />
            About
          </motion.p>
          <motion.h1
            style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(3rem, 6vw, 6rem)', fontWeight: 300, color: 'var(--text)', letterSpacing: '0.01em', lineHeight: 1.05, marginBottom: '1.5rem' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            Burke<br />Ruder
          </motion.h1>
          <motion.div
            style={{ width: 40, height: 1, background: 'var(--accent)', marginBottom: '1.5rem' }}
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            style2={{ transformOrigin: 'left' }}
            transition={{ delay: 0.5, duration: 0.6 }}
          />
          <motion.p
            style={{ fontSize: '0.85rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 300, lineHeight: 2 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.6 }}
          >
            Senior Account Executive<br />
            <span style={{ color: 'var(--accent)' }}>Cloudflare</span><br />
            Austin, Texas
          </motion.p>
          <motion.p
            style={{ marginTop: '3rem', fontFamily: 'var(--serif)', fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', fontWeight: 300, fontStyle: 'italic', color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 320 }}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            "Just curious.<br />That's the whole thing."
          </motion.p>
        </motion.div>
      </div>

      {/* Mobile hero (stacked) */}
      <style>{`
        @media (max-width: 768px) {
          .about-split { grid-template-columns: 1fr !important; }
          .about-split > div:first-child { height: 60vw !important; min-height: 300px; }
          .about-split > div:last-child { padding: 2.5rem 1.25rem !important; }
        }
      `}</style>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid var(--border)' }}>
        {[
          { num: '20+', label: 'Years experience' },
          { num: '$50M+', label: 'Procurement managed' },
          { num: '4×', label: "Top Performer" },
          { num: '100%+', label: 'Quota attainment' },
        ].map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ padding: '2.5rem 2rem', borderRight: i < 3 ? '1px solid var(--border)' : 'none' }}
          >
            <div style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, lineHeight: 1, marginBottom: '0.4rem' }}>
              <span style={{ color: 'var(--accent)' }}>{s.num}</span>
            </div>
            <div style={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              {s.label}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Credentials + Domain Expertise */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '3.5rem 5vw' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>

          {/* Domain chips */}
          <motion.p
            style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '1.25rem' }}
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          >
            Domain Expertise
          </motion.p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2.5rem' }}>
            {['Zero Trust', 'SASE', 'DDoS Protection', 'DNS Security', 'Edge Networking', 'Cloud Security', 'Enterprise SaaS', 'MEDDPICC', 'Channel Sales', 'Pipeline Development'].map((d, i) => (
              <motion.span
                key={d}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.4 }}
                style={{
                  padding: '0.35rem 0.85rem',
                  fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--accent)',
                  border: '1px solid var(--border)', borderRadius: '2px',
                }}
              >
                {d}
              </motion.span>
            ))}
          </div>

          {/* Cert row */}
          <motion.p
            style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '1.25rem' }}
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          >
            Certifications &amp; Learning
          </motion.p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {[
              { label: 'AWS Certified Cloud Practitioner', icon: '☁', status: 'earned' },
              { label: 'NABCEP PV Installation Professional', icon: '⚡', status: 'earned' },
              { label: 'AWS Solutions Architect – Associate', icon: '☁', status: 'progress' },
              { label: 'TryHackMe · SOC Level 1', icon: '🛡', status: 'progress' },
            ].map((c, i) => (
              <motion.span
                key={c.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.45rem 1rem',
                  fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.06em',
                  color: c.status === 'earned' ? 'var(--text)' : 'var(--text-muted)',
                  background: c.status === 'earned' ? 'var(--bg3)' : 'transparent',
                  border: c.status === 'earned' ? '1px solid var(--border)' : '1px dashed var(--border)',
                  borderRadius: '2px',
                }}
              >
                <span style={{ color: 'var(--accent)', fontSize: '0.8rem' }}>{c.icon}</span>
                {c.status === 'progress' && <span style={{ fontSize: '0.6rem', color: 'var(--accent)' }}>●</span>}
                {c.label}
                {c.status === 'progress' && <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>in progress</span>}
              </motion.span>
            ))}
          </div>
        </div>
      </div>

      {/* Bio + Experience */}
      <div style={{ padding: '6rem 5vw', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5rem', alignItems: 'start' }}>

          {/* Bio */}
          <div>
            <motion.p
              style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            >
              <span style={{ display: 'block', width: 28, height: 1, background: 'var(--accent)' }} />
              Background
            </motion.p>
            <motion.h2
              style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: '2rem' }}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              Curious by nature.<br />Built by experience.
            </motion.h2>
            {[
              "I started my career loading package cars at UPS at 3am. No plan, just work. Over the next twelve years I touched every part of that operation — logistics, management, team-building, problem-solving under pressure. It was the best business school I could have attended.",
              "When the solar industry started to boom, I made a move into procurement. I spent several years at Lighthouse Solar building supplier relationships and managing $50M+ in materials. That chapter taught me how to think in systems, manage complexity, and make big decisions with incomplete information.",
              "Then I bet on myself again. I joined Cloudflare — one of the fastest-growing infrastructure companies in the world — as a BDR. Within a couple of years I was a Senior AE selling Zero Trust, SASE, DDoS protection, and DNS security to CISOs and CTOs at enterprise accounts. I've hit 100%+ quota every year, earned 4× Top Performer recognition, and I use MEDDPICC to run every deal.",
              "I'm now building deeper technical fluency in cybersecurity and cloud. I hold an AWS Cloud Practitioner certification and I'm actively pursuing my AWS Solutions Architect cert and working through security operations training. The goal: bridge the gap between enterprise sales and solutions engineering.",
              "Outside of work I'm a photographer. I shoot street and urban scenes around Austin — always looking for the light, the moment, the thing you weren't supposed to see. Same instinct, different medium.",
            ].map((para, i) => (
              <motion.p
                key={i}
                style={{ fontSize: '0.93rem', lineHeight: 1.9, color: 'var(--text-muted)', fontWeight: 300, marginBottom: '1.25rem' }}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                {para}
              </motion.p>
            ))}
          </div>

          {/* Experience */}
          <div>
            <motion.p
              style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            >
              <span style={{ display: 'block', width: 28, height: 1, background: 'var(--accent)' }} />
              Experience
            </motion.p>
            {experience.map((item, i) => (
              <ExpCard key={item.years + item.company} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>

      {/* Contact strip */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '4rem 5vw', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
        <div>
          <p style={{ fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Get in touch</p>
          <a href="mailto:burke.ruder@gmail.com" style={{ fontFamily: 'var(--serif)', fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)', borderBottom: '1px solid var(--accent)', paddingBottom: 2 }}>
            Email Me
          </a>
        </div>
        <a href="https://www.linkedin.com/in/burke-ruder/" target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', paddingBottom: 2 }}>
          View LinkedIn →
        </a>
      </div>
    </div>
  );
}
